import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { FiCheck, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi';
import { onPermissionError } from '../../../utils/permissionErrorBus';

// Contexto para las notificaciones
const NotificationContext = createContext();

// Hook personalizado para usar las notificaciones
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications debe ser usado dentro de NotificationProvider');
  }
  return context;
};

// Componente individual de notificación
const NotificationItem = ({ notification, onClose }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <FiCheck className="notification-icon" />;
      case 'error':
        return <FiAlertCircle className="notification-icon" />;
      case 'warning':
        return <FiAlertTriangle className="notification-icon" />;
      case 'info':
      default:
        return <FiInfo className="notification-icon" />;
    }
  };

  return (
    <div className={`notification notification--${notification.type}`}>
      {getIcon()}
      <div className="notification-content">
        {notification.title && (
          <div className="notification-title">{notification.title}</div>
        )}
        <div className="notification-message">{notification.message}</div>
      </div>
      <button
        className="notification-close"
        onClick={() => onClose(notification.id)}
        aria-label="Cerrar notificación"
      >
        <FiX />
      </button>
    </div>
  );
};

// Contenedor de notificaciones
const NotificationContainer = ({ notifications, onClose }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="notification-container">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      ))}
    </div>
  );
};

// Provider de notificaciones
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const addNotification = useCallback((notification) => {
    const id = `${Date.now()}-${Math.round(Math.random() * 1e6)}`;
    const newNotification = {
      id,
      type: 'info',
      autoClose: true,
      duration: 5000,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-cerrar si está habilitado
    if (newNotification.autoClose) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, [removeNotification]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Métodos de conveniencia
  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({ ...options, message, type: 'success' });
  }, [addNotification]);

  const showError = useCallback((message, options = {}) => {
    return addNotification({
      ...options,
      message,
      type: 'error',
      autoClose: false // Errores no se cierran automáticamente
    });
  }, [addNotification]);

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({
      ...options,
      message,
      type: 'warning',
      duration: 7000 // Warnings duran más tiempo
    });
  }, [addNotification]);

  // Los interceptores de axios avisan por aqui cuando el backend devuelve 403.
  // Sin esto, a la pantalla le faltaban los datos y no se explicaba por que.
  useEffect(() => onPermissionError(({ message }) => {
    showWarning(message, { title: 'Permiso insuficiente', duration: 9000 });
  }), [showWarning]);

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({ ...options, message, type: 'info' });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer
        notifications={notifications}
        onClose={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

// Hook con métodos específicos para operaciones CRUD
export const useCrudNotifications = () => {
  const { showSuccess, showError, showWarning } = useNotifications();

  return {
    onCreateSuccess: (entityName = 'registro') =>
      showSuccess(`${entityName} creado exitosamente`),
    onUpdateSuccess: (entityName = 'registro') =>
      showSuccess(`${entityName} actualizado exitosamente`),
    onDeleteSuccess: (entityName = 'registro') =>
      showSuccess(`${entityName} eliminado exitosamente`),
    onCreateError: (error, entityName = 'registro') =>
      showError(`Error al crear ${entityName}: ${error}`),
    onUpdateError: (error, entityName = 'registro') =>
      showError(`Error al actualizar ${entityName}: ${error}`),
    onDeleteError: (error, entityName = 'registro') =>
      showError(`Error al eliminar ${entityName}: ${error}`),
    onValidationError: () =>
      showWarning('Por favor corrige los errores en el formulario'),
    onUnsavedChanges: () =>
      showWarning('Tienes cambios sin guardar')
  };
};

export default NotificationProvider;
